import React, { useEffect, useState } from 'react';
import { useAuth0 } from '../../../auth/useAuth0';

const CHAT_BASE = (process.env.REACT_APP_CHAT_SERVICE_BASE_URL || '').replace(/\/graphql$/, '');

/** Blob URLs already fetched in this tab, by image key (survives re-renders/polling). */
const cache = new Map<string, string>();

/**
 * A chat photo. Chat photos have NO shareable link: chat-service streams
 * `GET /images/<key>` only with the caller's bearer and only to a participant of
 * the conversation. We fetch with the token and render a blob: URL, which only
 * exists inside this browser profile — pasting it into an anonymous window shows
 * nothing.
 */
export const ChatImage: React.FC<{ imageKey: string; alt: string; className?: string }> = ({ imageKey, alt, className }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [url, setUrl] = useState<string | null>(cache.get(imageKey) ?? null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (cache.has(imageKey)) {
            setUrl(cache.get(imageKey)!);
            return;
        }
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                const res = await fetch(`${CHAT_BASE}/images/${imageKey}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) throw new Error(String(res.status));
                const blobUrl = URL.createObjectURL(await res.blob());
                cache.set(imageKey, blobUrl);
                if (!cancelled) setUrl(blobUrl);
            } catch {
                if (!cancelled) setFailed(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [imageKey, getAccessTokenSilently]);

    if (failed) return <span className={`${className ?? ''} bubble-img--missing`} title={alt} aria-label={alt} />;
    if (!url) return <span className={`${className ?? ''} bubble-img--loading`} aria-busy="true" />;
    return <img src={url} alt={alt} className={className} onClick={() => window.open(url, '_blank')} />;
};

export default ChatImage;
